import { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import Modal from "@shared/v2/Modal";
import { PlusSolidV6 } from "@shared/v2/Icomoon";
import { css, cx } from "@emotion/css";
import RolesRow from "./RolesRow";
import Loading from "../../../Goals/components/Loader";

class ManageAccessModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      primaryAgent: props.primaryAgent,
      selectedTeamMembers: props.nonPrimaryTeamMembers || [],
      allAssignableAgents: [],
      allAssignableRoles: props.allAssignableRoles || [],
      primaryAgentSet: false,
    };
  }

  componentDidMount() {
    axios
      .get("/account_active_agents", {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((response) => {
        this.setState({ allAssignableAgents: response.data });
      })
      .catch(console.log);
  }

  onClose = () => {
    const { closeManageAccessModal } = this.props;
    closeManageAccessModal();
  };

  handleRemoveAgent = (agentId, roleId) => {
    const { selectedTeamMembers } = this.state;
    const agentToRemove = selectedTeamMembers.find(
      (agent) => agent.agent_id === agentId && agent.role_id === roleId,
    );
    if (agentToRemove) {
      this.setState((prevState) => {
        if (agentToRemove.id) {
          agentToRemove.destroy = true;
          return {
            selectedTeamMembers: prevState.selectedTeamMembers.map((agent) =>
              agent === agentToRemove ? agentToRemove : agent,
            ),
          };
        }
        return {
          selectedTeamMembers: prevState.selectedTeamMembers.filter((agent) => agent !== agentToRemove),
        };
      });
    }
  };

  handleAddTeamMembers = () => {
    this.setState(({ selectedTeamMembers }) => ({
      selectedTeamMembers: [
        ...selectedTeamMembers,
        {
          agent_id: "",
          id: null,
          image: "",
          name: "",
          primary: "",
          role_id: null,
          user_type: "",
        },
      ],
    }));
  };

  handleAddAgent = (teamMember, i) => {
    this.setState(({ selectedTeamMembers }) => ({
      selectedTeamMembers: selectedTeamMembers.map((tm, index) =>
        index === i ? { ...teamMember, id: null } : tm,
      ),
    }));
  };

  handleAddRole = (roleId, index) => {
    const { selectedTeamMembers } = this.state;
    this.setState({
      selectedTeamMembers: selectedTeamMembers.map((selectedTeamMember, i) =>
        index === i ? { ...selectedTeamMember, role_id: roleId } : selectedTeamMember,
      ),
    });
  };

  handleSelectPrimary = (selection) => {
    this.setState({
      primaryAgentSet: true,
      primaryAgent: { ...selection, primary: true },
    });
  };

  agentPermissions = () => {
    const { selectedTeamMembers } = this.state;
    return selectedTeamMembers.reduce((acc, agent) => {
      if (agent.user_type === "Agent") {
        acc[agent.agent_id] = agent.destroy ? "none" : "all";
      }
      return acc;
    }, {});
  };

  handleManageAccess = (e) => {
    const { personId, closeManageAccessModal, handleUpdateAccess } = this.props;
    const { selectedTeamMembers, primaryAgentSet, primaryAgent } = this.state;
    e.preventDefault();
    const params = {
      people_ids: [personId],
      agent_permissions: this.agentPermissions(),
      team_members: [...selectedTeamMembers],
      authenticity_token: ReactOnRails.authenticityToken(),
    };

    if (primaryAgentSet) {
      params.primary_agent_set = true;
      params.primary_agent = [primaryAgent.id];
    }

    axios
      .post("/people_permissions", params)
      .then(() => {
        this.setState({ primaryAgentSet: false });
        handleUpdateAccess();
        closeManageAccessModal();
      })
      .catch((error) => {
        console.log("Error: ", error);
      });
  };

  renderAgentValue = ({
    componentProps,
    componentProps: {
      getStyles,
      innerProps,
      isDisabled,
      isFocused,
      isSelected,
      data: { email, name, agent_id: agentId, primary: isPrimary, user_type: type },
    },
  }) => {
    const { primaryAgent } = this.state;

    const classNameFromVariant = cx(
      css(getStyles("singleValue", componentProps)),
      {
        "single-value": true,
        "single-value--is-disabled": isDisabled,
        "single-value--is-focused": isFocused,
        "single-value--is-selected": isSelected,
      },
      "tw-flex tw-justify-between",
    );

    const userType = isPrimary === true || primaryAgent.id === agentId ? "PRIMARY AGENT" : type.toUpperCase();

    return (
      <div className={classNameFromVariant} {...innerProps}>
        <span className="tw-text-gray-75">
          {name} ({email})
        </span>
        <span>{userType}</span>
      </div>
    );
  };

  renderAgentOption = ({
    componentProps,
    componentProps: { getStyles, innerRef, innerProps, isDisabled, isFocused, isSelected },
  }) => {
    const classNameFromVariant = cx(css(getStyles("option", componentProps)), {
      option: true,
      "option--is-disabled": isDisabled,
      "option--is-focused": isFocused,
      "option--is-selected": isSelected,
    });

    return (
      <div className={classNameFromVariant} ref={innerRef} {...innerProps} style={{ padding: "8px" }}>
        {this.renderAgentValue({ componentProps })}
      </div>
    );
  };

  render() {
    const { nonPrimaryTeamMembers } = this.props;
    const { primaryAgent, selectedTeamMembers, allAssignableAgents, allAssignableRoles } = this.state;
    const selectedRolesIds = selectedTeamMembers.filter((tm) => !tm.destroy).map((tm) => tm.role_id);
    const availableRoles = allAssignableRoles.filter((role) => selectedRolesIds.indexOf(role.id) === -1);

    // For some reason, `primaryAgent` is not _completely_ populated at first
    // (notably: the email - which we need to render - is not present), so we
    // need to search in `allAssignableAgents`. Subsequent renders after
    // selecting an option are fine, so we can skip the search in that case.
    const selectedAgent = primaryAgent.email
      ? primaryAgent
      : allAssignableAgents.find((agent) => agent.agent_id === primaryAgent.agent_id);

    return (
      <Modal
        id="manage-access-modal"
        show
        onHide={this.onClose}
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
      >
        <Modal.Header
          description="Making another user a team member gives a user the ability to view and edit a person's information. Team members do not need a role to gain access. Access can be revoked only for team members with the user type of agent. The account owner and admins have access to everyone."
          title="Manage Team"
          closeButton
        />

        <Modal.Body className="tw-flex-1 tw-overflow-auto tw-relative tw-flex tw-flex-col tw-gap-[24px]">
          {allAssignableAgents.length === 0 && <Loading />}

          {allAssignableAgents.length > 0 && (
            <Dropdown
              label="Primary Agent"
              menuShouldComeToFront
              isSearchable
              isClearable={false}
              placeholder="Select..."
              onChange={(e) => this.handleSelectPrimary(e)}
              options={allAssignableAgents}
              value={selectedAgent}
              isOptionSelected={(option) =>
                selectedAgent ? option.agent_id === selectedAgent.agent_id : false
              }
              components={{
                Option: this.renderAgentOption,
                SingleValue: this.renderAgentValue,
              }}
              // Since we're using a custom shape for options and writing
              // custom renderers, we need to write our own filterOption
              // as well so isSearchable works as expected.
              filterOption={(candidate, input) => {
                // Show all options if the user hasn't typed anything in yet
                if (!input) {
                  return true;
                }

                const lowercasedInput = input.toLowerCase();
                const option = candidate.data;

                // Otherwise, only show candidates that contain the input string
                // in them
                return (
                  option.name.toLowerCase().includes(lowercasedInput) ||
                  option.email.toLowerCase().includes(lowercasedInput)
                );
              }}
            />
          )}

          {selectedTeamMembers.length > 0 && (
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <RolesRow
                selectedTeamMembers={selectedTeamMembers}
                allAssignableRoles={allAssignableRoles}
                availableRoles={availableRoles}
                allAssignableAgents={allAssignableAgents}
                nonPrimaryTeamMembers={nonPrimaryTeamMembers}
                handleAddAgent={this.handleAddAgent}
                handleAddRole={this.handleAddRole}
                handleRemoveAgent={this.handleRemoveAgent}
              />
            </div>
          )}

          {availableRoles.length > 0 && (
            <Button
              schema="secondary"
              className="tw-self-start tw-flex tw-items-center tw-gap-[4px]"
              onClick={this.handleAddTeamMembers}
            >
              <PlusSolidV6 /> Add Team Member
            </Button>
          )}
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
          <Button size="medium" schema="tertiary" onClick={this.onClose}>
            Cancel
          </Button>
          <Button size="medium" onClick={this.handleManageAccess}>
            Save Team
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

ManageAccessModal.propTypes = {
  personId: PropTypes.number.isRequired,
  primaryAgent: PropTypes.shape().isRequired,
  nonPrimaryTeamMembers: PropTypes.arrayOf(PropTypes.shape()),
  allAssignableRoles: PropTypes.arrayOf(PropTypes.shape()),
  closeManageAccessModal: PropTypes.func.isRequired,
  handleUpdateAccess: PropTypes.func.isRequired,
};

ManageAccessModal.defaultProps = {
  nonPrimaryTeamMembers: [],
  allAssignableRoles: [],
};

export default ManageAccessModal;
